import { useMemo, useState } from 'react';
import { ClientOnly } from '@tanstack/react-router';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { money, type ComparisonOffer } from '@/lib/comparison';
import { Button } from '@/components/ui/button';

export type HistoryPoint = { day: string; brand_id: string; price: number };

const COLORS = ['hsl(var(--primary))', 'hsl(var(--success))', 'hsl(var(--destructive))', 'hsl(215 45% 55%)', 'hsl(var(--muted-foreground))'];
const fmtDay = (d: string) => d.slice(8, 10) + '/' + d.slice(5, 7);

export function priceMoment(history: HistoryPoint[], current: number) {
  const daily = dailyMin(history);
  if (daily.length < 7) return null;
  const avg = daily.reduce((s, d) => s + d.min, 0) / daily.length;
  if (current <= avg * 0.97) return { label: 'Preço abaixo da média', tone: 'good' as const, avg };
  if (current >= avg * 1.03) return { label: 'Preço acima da média', tone: 'bad' as const, avg };
  return { label: 'Preço na média', tone: 'neutral' as const, avg };
}

function dailyMin(history: HistoryPoint[]) {
  const m = new Map<string, number>();
  for (const h of history) m.set(h.day, Math.min(m.get(h.day) ?? Infinity, h.price));
  return [...m.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([day, min]) => ({ day, min }));
}

export function PriceHistory({ history, offers, compact = false }: { history: HistoryPoint[]; offers: ComparisonOffer[]; compact?: boolean }) {
  const [range, setRange] = useState(90);
  const brands = useMemo(() => [...new Map(offers.map(o => [o.brand_id, o.brand.name])).entries()].slice(0, 5), [offers]);
  const firstDay = history[0]?.day;
  const { rows, stats } = useMemo(() => {
    const since = new Date(Date.now() - range * 86400_000).toISOString().slice(0, 10);
    const h = history.filter(x => x.day >= since);
    const days = dailyMin(h);
    const rows = days.map(d => {
      const row: Record<string, number | string> = { day: d.day, Menor: d.min };
      for (const [id] of brands) { const p = h.find(x => x.day === d.day && x.brand_id === id); if (p) row[id] = p.price; }
      return row;
    });
    const mins = days.map(d => d.min);
    return { rows, stats: mins.length ? { min: Math.min(...mins), max: Math.max(...mins), avg: mins.reduce((a, b) => a + b, 0) / mins.length } : null };
  }, [history, range, brands]);
  const totalDays = dailyMin(history).length;
  const stable = totalDays >= 2 && new Set(history.map(h => h.price)).size === 1;
  const firstLabel = firstDay ? fmtDay(firstDay) : '';

  return <section className={compact ? '' : 'container py-10'} aria-labelledby="price-history-title">
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-3">
      <div className="min-w-0"><p className="text-[10px] font-bold uppercase text-primary">Histórico</p><h2 id="price-history-title" className="mt-1 text-lg font-bold">Evolução do preço</h2></div>
      {totalDays >= 2 && <div className="flex shrink-0 gap-1 rounded-md border p-1" role="group" aria-label="Período">{[30, 90, 180].map(d => <Button key={d} type="button" size="sm" variant={range === d ? 'default' : 'ghost'} onClick={() => setRange(d)} aria-pressed={range === d} className="h-7 px-2 text-xs">{d}d</Button>)}</div>}
    </div>
    {totalDays < 2 ? <p className="mt-6 rounded-md border bg-muted/30 p-5 text-sm text-muted-foreground">Começamos a acompanhar este produto{firstDay ? ` em ${fmtDay(firstDay)}` : ''}. O gráfico aparece a partir do segundo dia de acompanhamento.</p> : <>
      <p className="mt-3 text-sm text-muted-foreground">{stable ? `Preço estável desde ${firstLabel}.` : `Acompanhando desde ${firstLabel}.`}</p>
      <div className={`mt-4 w-full rounded-md border bg-card p-2 ${compact ? 'h-40' : 'h-64'}`}>
        <ClientOnly fallback={<div className="h-full w-full animate-pulse rounded bg-muted/40" />}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tickFormatter={v => `R$${Math.round(v)}`} width={64} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" domain={['auto', 'auto']} />
              <Tooltip formatter={(v: number, k: string) => [money(v), k === 'Menor' ? 'Menor preço' : brands.find(b => b[0] === k)?.[1] ?? k]} labelFormatter={(l: string) => fmtDay(l)} contentStyle={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: 6 }} />
              <Line type="monotone" dataKey="Menor" stroke="hsl(var(--foreground))" strokeWidth={2.5} dot={false} />
              {brands.map(([id], i) => <Line key={id} type="monotone" dataKey={id} stroke={COLORS[i % COLORS.length]} strokeWidth={1.5} dot={false} strokeDasharray="4 3" connectNulls />)}
            </LineChart>
          </ResponsiveContainer>
        </ClientOnly>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"><span><span className="mr-1 inline-block h-0.5 w-4 bg-foreground align-middle" />Menor preço</span>{brands.map(([id, name], i) => <span key={id}><span className="mr-1 inline-block h-0.5 w-4 align-middle" style={{ background: COLORS[i % COLORS.length] }} />{name}</span>)}</div>
      {stats && <dl className="mt-4 grid grid-cols-3 border-y py-3 text-center">
        <div><dt className="text-[10px] text-muted-foreground">Menor</dt><dd className="text-sm font-bold tabular-nums">{money(stats.min)}</dd></div>
        <div><dt className="text-[10px] text-muted-foreground">Média</dt><dd className="text-sm font-bold tabular-nums">{money(stats.avg)}</dd></div>
        <div><dt className="text-[10px] text-muted-foreground">Maior</dt><dd className="text-sm font-bold tabular-nums">{money(stats.max)}</dd></div>
      </dl>}
    </>}
  </section>;
}
